/**
 * Redux action creators for admin operations (companies, users, groups, roles, permissions).
 *
 * @module redux/admin/action
 * @author Snowfly
 * @version 1.0.0
 */
import axios from 'axios';
import admin from '../../permissions/admin';
import config from '../../config';
import {
    FETCH_COMPANY_FAIL,
    FETCH_COMPANY_REQUEST,
    FETCH_COMPANY_ROLES_FAIL,
    FETCH_COMPANY_ROLES_REQUEST,
    FETCH_COMPANY_ROLES_SUCCESS,
    FETCH_COMPANY_SUCCESS,
    FETCH_REQUEST,
    FETCH_FAILURE,
    FETCH_STATUS,
    FETCH_SUB_STATUS,
    FETCH_ROLES,
    FETCH_GROUPS,
    SAVE_USER,
    SAVE_GROUP,
    FETCH_EMPLOYEES,
    ADD_ROLES,
    EDIT_GROUP_IN_USER,
    RESET,
    SAVE_TOOLTIP,
    FETCH_NOTIFICATION,
    RESET_NOTIFICATION,
    FETCH_COMPANIES,
} from './adminTypes';

/**
 * Represents an action creator for resetting notifications.
 *
 * This function returns an action object with the type `RESET_NOTIFICATION`.
 * It can be used in a Redux setup to reset or clear notification-related
 * state in the application's store.
 *
 * @function resetNotification
 * @returns {Object} Action object with the `type` property set to `RESET_NOTIFICATION`.
 */
export const resetNotification = () => ({
    type: RESET_NOTIFICATION,
});

/**
 * Action creator for initiating a company fetch request.
 *
 * Constructs an action with the type `FETCH_COMPANY_REQUEST` and includes
 * the provided payload.
 *
 * @function
 * @param {Object} payload - The payload containing the necessary data for the request.
 * @returns {Object} Action object with type `FETCH_COMPANY_REQUEST` and the given payload.
 */
export const fetchCompanyRequest = (payload) => ({
    type: FETCH_COMPANY_REQUEST,
    payload,
});

/**
 * Action creator for successfully fetching company information.
 *
 * This function is used to create an action that represents the successful fetch
 * of company data. It takes the fetched data as an argument and returns an action
 * object with the appropriate action type and payload.
 *
 * @param {Object} payload - The data retrieved from the fetch operation, typically
 * containing company details.
 * @returns {Object} An action object with type `FETCH_COMPANY_SUCCESS` and the provided payload.
 */
export const fetchCompanySuccess = (payload) => ({
    type: FETCH_COMPANY_SUCCESS,
    payload,
});

/**
 * Action creator for handling the failure of a company-related operation.
 * Constructs an action with the specified payload and the type `FETCH_COMPANY_FAIL`.
 *
 * @param {Object} payload - The error details or additional information related to the failure.
 * @returns {Object} The action object containing the type and payload.
 */
export const fetchCompanyFail = (payload) => ({
    type: FETCH_COMPANY_FAIL,
    payload,
});

// Fetch all companies
/**
 * fetchCompany is an asynchronous action creator that dispatches a network
 * request to retrieve a list of companies. It utilizes Axios to send a GET
 * request to the API endpoint and manages the request's lifecycle by
 * dispatching corresponding actions for the request's initiation,
 * successful completion, or failure.
 *
 * This function sends an HTTP GET request to the endpoint defined in the
 * application's configuration. It includes an Authorization token and other
 * custom headers in the request. Upon success, it dispatches an action with
 * the received data. If the request fails, an error action is dispatched.
 *
 * @function fetchCompany
 * @returns {Function} A function that takes dispatch as an argument and
 * initiates the network request.
 */
export const fetchCompany = () => (dispatch) => {
    dispatch(fetchCompanyRequest);
    axios({
        method: 'GET',
        url: `${config.API_BASE_URL}/api/v1/company/list`,
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(fetchCompanySuccess(res.data)))
        .catch((err) => dispatch(fetchCompanyFail(err)));
};

/**
 * Action creator for initiating the fetch company roles request.
 *
 * This function constructs an action with the type `FETCH_COMPANY_ROLES_REQUEST`
 * and includes the provided payload as part of the action object.
 *
 * @function
 * @param {Object} payload - The payload to be sent with the action.
 * @returns {Object} The action object containing the type and payload.
 */
export const fetchCompanyRolesRequest = (payload) => ({
    type: FETCH_COMPANY_ROLES_REQUEST,
    payload,
});

/**
 * Action creator function for successfully fetching company roles.
 *
 * @function fetchCompanyRolesSuccess
 * @param {Object} payload - The data retrieved from the successful company roles fetch operation.
 * @returns {Object} Action object containing the type `FETCH_COMPANY_ROLES_SUCCESS` and the provided payload.
 */
export const fetchCompanyRolesSuccess = (payload) => ({
    type: FETCH_COMPANY_ROLES_SUCCESS,
    payload,
});

/**
 * Action creator function for signaling failure in fetching company roles.
 *
 * @function
 * @name fetchCompanyRolesFail
 * @param {any} payload - The data or information associated with the failure of fetching company roles.
 * @returns {Object} An action object containing the type 'FETCH_COMPANY_ROLES_FAIL' and the provided payload.
 */
export const fetchCompanyRolesFail = (payload) => ({
    type: FETCH_COMPANY_ROLES_FAIL,
    payload,
});

/**
 * Represents an action creator function to fetch a notification list.
 *
 * @function
 * @param {Object} data - The payload containing details for fetching the notification list.
 * @returns {Object} - The action object containing the type of action and the payload.
 */
export const fetchNotifyList = (data) => ({
    type: FETCH_NOTIFICATION,
    payload: data,
});

/**
 * Action creator for successful retrieval of companies.
 *
 * This function generates an action object to signify the successful
 * fetching of company data from a data source. The generated action
 * object includes a type property and the data retrieved as the payload.
 *
 * @param {Object} data - The payload containing the fetched company data.
 * @returns {Object} The action object with type `FETCH_COMPANIES` and the provided `data` as payload.
 */
export const fetchCompaniesSuccess = (data) => ({
    type: FETCH_COMPANIES,
    payload: data
});

/**
 * Function representing an action creator for initiating a fetch request.
 *
 * @constant
 * @type {Function}
 * @returns {Object} An action object containing the type property with the FETCH_REQUEST value.
 */
export const fetchRequest = () => ({
    type: FETCH_REQUEST,
});

/**
 * Represents an action creator for a failed fetch operation.
 *
 * @function fetchFailure
 * @param {Object} data - The data representing the error or failure details.
 * @returns {Object} An action object with `type` as FETCH_FAILURE and the provided `data` as the payload.
 */
export const fetchFailure = (data) => ({
    type: FETCH_FAILURE,
    payload: data,
});

/**
 * Constructs an action object to fetch the status.
 *
 * @param {Object} data - The data object containing necessary information for the action.
 * @returns {Object} The action object containing the type and payload properties.
 */
export const fetchStatus = (data) => ({
    type: FETCH_STATUS,
    payload: data,
});

/**
 * Action creator function for fetching subscription status.
 *
 * This function creates an action object to be dispatched to the Redux store
 * for updating the state with the subscription status data. The action object
 * consists of a `type` indicating the action type and a `payload` carrying
 * the data related to the subscription status.
 *
 * @param {any} data - The subscription status data to be included as the payload.
 * @returns {Object} The action object containing the action type and payload.
 */
export const fetchSubStatus = (data) => ({
    type: FETCH_SUB_STATUS,
    payload: data,
});

/**
 * Creates an action object for fetching roles.
 *
 * @function fetchRoles
 * @param {Object} data - The payload containing data for the roles to be fetched.
 * @returns {Object} An action object containing the type and the payload.
 */
export const fetchRoles = (data) => ({
    type: FETCH_ROLES,
    payload: data,
});

/**
 * Action creator function to fetch groups.
 *
 * This function generates an action object used to fetch groups data.
 * The action object contains the type of action to be performed and
 * the payload data associated with the request.
 *
 * @param {Object} data - The data to be used as the payload for the action.
 * @returns {Object} An action object with `type` and `payload` properties.
 */
export const fetchGroups = (data) => ({
    type: FETCH_GROUPS,
    payload: data,
});

/**
 * Action creator function to fetch employee data.
 *
 * @function fetchEmploy
 * @param {Object} data - The data containing employee details to be fetched.
 * @returns {Object} The action object with type `FETCH_EMPLOYEES` and the provided data as payload.
 */
export const fetchEmploy = (data) => ({
    type: FETCH_EMPLOYEES,
    payload: data,
});

/**
 * A function that creates an action object for saving user data.
 *
 * @function
 * @param {Object} data - The user data to be saved.
 * @param {Object} res - The response object containing additional details.
 * @param {Object} res.data - The data property of the response object that carries the result.
 * @returns {Object} An action object with the `type` property set to `SAVE_USER`
 *                   and `result` property containing the `res.data`.
 */
export const addUserData = (data, res) => ({
    type: SAVE_USER,
    result: res.data,
});

/**
 * A function that constructs and returns an object representing a group save action.
 *
 * @param {Object} data - The input data used for saving the group.
 * @param {Object} res - The response object containing the `data` property.
 * @returns {Object} An object containing the type of action and the extracted result data.
 */
export const addGroupData = (data, res) => ({
    type: SAVE_GROUP,
    result: res.data,
});

/**
 * Function to create an action for adding a role.
 *
 * @function addRole
 * @param {Object} data - The data to be used as the payload for the action.
 * @param {Object} res - The response object containing the role information.
 * @returns {Object} An action object with the type of `ADD_ROLES`, the `result` derived from the response, and the provided payload data.
 */
export const addRole = (data, res) => ({
    type: ADD_ROLES,
    result: res.data.id,
    payload: data,
});

/*export const fetchSelectStatus = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.MOCK_URL}/selectStatus`)
        .then((res) => dispatch(fetchStatus(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};
export const fetchSelectSubStatus = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.MOCK_URL}/selectSubStatus`)
        .then((res) => dispatch(fetchSubStatus(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};*/

/**
 * A Redux-thunk action creator that fetches the roles associated with a specific company
 * based on its ID. It dispatches related actions for initiating the request, handling
 * a successful response, or dealing with an error.
 *
 * @function fetchCompanyRoles
 * @param {string} id - The unique identifier of the company whose roles are to be fetched.
 * @returns {Function} A Redux-thunk function that performs an HTTP GET request to retrieve
 * the company's roles and dispatches the appropriate actions based on the request's result.
 */
export const fetchCompanyRoles = (id) => (dispatch) => {
    dispatch(fetchCompanyRolesRequest);
    axios({
        method: 'GET',
        url: `${config.API_BASE_URL}/api/v1/company/role-list?company_id=${id}`,
        headers: {
            Authorization: config.getToken(),
            task: admin.roles.view.task,
            type: admin.roles.view.type,
        },
    })
        .then((res) => dispatch(fetchCompanyRolesSuccess(res.data)))
        .catch((err) => dispatch(fetchCompanyRolesFail(err)));
};

/**
 * An asynchronous function that dispatches actions to fetch a list of roles from the API.
 * This function makes an HTTP GET request to the `role-list` endpoint and handles the response
 * or potential errors by dispatching appropriate actions.
 *
 * @function fetchSelectRoles
 * @returns {Function} A thunk function that invokes dispatch actions.
 */
export const fetchSelectRoles = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.API_BASE_URL}/api/v1/company/role-list`,
        {
            headers: {
                Authorization: config.getToken(),
                task: null,
                type: null,
            },
        })
        .then((res) => dispatch(fetchRoles(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * A function to fetch the role list data from the API and handle the request lifecycle.
 * Dispatches a request action before initiating the API call.
 * On successful response, it dispatches an action to update the state with the fetched roles data.
 * In case of an error, it dispatches an action to handle the failure.
 *
 * This function utilizes axios for making HTTP requests and executes within a Redux Thunk middleware.
 *
 * @function
 * @param {Function} dispatch - The Redux dispatch function to trigger state updates.
 * @returns {Promise<void>} A promise representing the completion of the asynchronous operation.
 */
export const fetchRoleData = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.API_BASE_URL}/api/v1/company/role-list`,
        {
            headers: {
                Authorization: config.getToken(),
                task: null,
                type: null,
            },
        })
        .then((res) => dispatch(fetchRoles(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * An asynchronous action creator function that fetches a list of groups for a selection dropdown.
 * It dispatches multiple actions to indicate the state of the API call.
 *
 * This function first dispatches a `fetchRequest` action to signal the beginning of the request.
 * It then makes an HTTP GET request to the `sims/group/listing` API endpoint using axios.
 * Upon receiving a successful response, it dispatches a `fetchGroups` action with the data returned by the API.
 * If an error occurs during the request, it dispatches a `fetchFailure` action with the error information.
 *
 * @function fetchSelectGroups
 * @returns {Function} A function that performs the asynchronous request and dispatches related actions.
 */
export const fetchSelectGroups = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.API_BASE_URL}/api/v1/sims/group/listing`,
        {
            headers: {
                Authorization: config.getToken(),
                task: null,
                type: null,
            },
        })
        .then((res) => dispatch(fetchGroups(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

// Save role
/**
 * Asynchronous action creator for saving a role.
 *
 * This function dispatches an HTTP POST request to create or update a role
 * based on whether the provided data contains an `_id` field. If `_id` exists,
 * it updates the role; otherwise, it creates a new one.
 *
 * @param {Object} data - The role data to be saved. Must include necessary role fields,
 *                        and optionally an `_id` to indicate an update operation.
 * @returns {Function} A thunk function that dispatches actions for the async operation.
 */
export const saveRole = (data) => async (dispatch) => {
    dispatch(fetchRequest);
    const task = data._id ? admin.roles.update.task : admin.roles.create.task;
    const type = data._id ? admin.roles.update.type : admin.roles.create.type;
    await axios.post(`${config.API_BASE_URL}/api/v1/roles`, data, {
        headers: {
            Authorization: config.getToken(),
            task,
            type,
        },
    })
        .then((res) => dispatch(addRole(data, res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Function to add or edit a group by making an asynchronous POST request to the server.
 *
 * @param {Object} data - The data object containing group information to be sent in the request body.
 * @returns {Function} A thunk function that accepts dispatch as an argument and handles the API request.
 *
 * The function:
 * - Dispatches a fetch request action before starting the API call.
 * - Sends a POST request to the server using Axios with required headers for authorization and task details.
 * - On successful response, dispatches an action to add the group data to the state.
 * - On request failure, dispatches an error handling action with the relevant error object.
 */
export const addGroup = (data) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.post(`${config.API_BASE_URL}/api/v1/groups/save-edit`, data, {
        headers: {
            Authorization: config.getToken(),
            task: admin.groups.create.task,
            type: admin.groups.create.type,
        },
    })
        .then((res) => dispatch(addGroupData(data, res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * A function to asynchronously fetch the minimal data of employees from the server,
 * dispatching appropriate actions during the request lifecycle.
 *
 * The function initiates a fetch request, sends an HTTP GET request to the specified
 * API endpoint for employee data, and handles both success and failure scenarios by
 * dispatching corresponding actions.
 *
 * @function fetchEmployees
 * @returns {Function} A thunk function for Redux to handle async actions, which dispatches
 *                     actions depending on the state of the HTTP request.
 */
export const fetchEmployees = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.API_BASE_URL}/api/v1/employee/minimal-data`,
        {
            headers: {
                Authorization: config.getToken(),
                task: admin.employee_list.view.task,
                type: admin.employee_list.view.type,
            },
        })
        .then((res) => dispatch(fetchEmploy(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * A function that dispatches an action to edit a group associated with a user.
 *
 * @function editGroupInUser
 * @param {Object} data - The data required to process the group edit operation.
 * @param {Object} res - The response object containing the updated group data.
 * @returns {Object} An action object with type `EDIT_GROUP_IN_USER` and the result of the update operation.
 */
export const editGroupInUser = (data, res) => ({
    type: EDIT_GROUP_IN_USER,
    result: res.data,
});

/**
 * Asynchronous action creator that adds a group to a user by sending a POST request to the API.
 *
 * @function addGroupToUser
 * @param {Object} data - The data payload containing the user and group information required for the operation.
 * @returns {Function} A redux-thunk function that dispatches actions based on the API call's success or failure.
 *
 * The function performs the following tasks:
 * - Dispatches a fetchRequest action to signal the start of the operation.
 * - Sends a POST request to the specified API endpoint with the provided user and group data.
 * - Includes necessary authentication and task/type headers in the request.
 * - On a successful response, dispatches the editGroupInUser action with the data and API response.
 * - On a failed response, dispatches the fetchFailure action with the encountered error.
 */
export const addGroupToUser = (data) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.post(`${config.API_BASE_URL}/api/v1/users/edit-group`, data, {
        headers: {
            Authorization: config.getToken(),
            task: admin.groups.update.task,
            type: admin.groups.update.type,
        },
    })
        .then((res) => dispatch(editGroupInUser(data, res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * A function to create an action object for resetting state in a Redux application.
 *
 * This function returns an action object with a type property of `RESET`.
 * The `RESET` constant is used to specify the type of action to dispatch, typically
 * to reset the application state to its initial value or a predefined default state.
 *
 * @function reset
 * @returns {Object} An action object with a `type` property set to `RESET`.
 */
export const reset = () => ({
    type: RESET,
});

/**
 * Updates the tooltip state with a new result.
 *
 * @function
 * @param {Object} res - The response object containing the new tooltip data.
 * @param {Object} res.data - The data object within the response to be saved.
 * @returns {Object} An action object with the type `SAVE_TOOLTIP` and the updated result data.
 */
export const updateTooltip = (res) => ({
    type: SAVE_TOOLTIP,
    result: res.data,
});

/**
 * Asynchronous action that sends a POST request to save permission details and updates the tooltip state.
 *
 * @param {Object} data - The data to be sent in the body of the POST request.
 * @param {string|number} id - The unique identifier for the permission details.
 * @returns {Function} A function that dispatches an action to initiate a fetch request,
 *                     processes the response to update the tooltip, or handles an error response.
 *
 * This function makes use of the `axios` library to send a POST request to the specified endpoint.
 * The request includes authorization and task type headers for security and logging purposes.
 * Dispatches relevant actions during the request lifecycle:
 * - dispatches `fetchRequest` to indicate the start of the request.
 * - dispatches `updateTooltip` with the updated data and response on success.
 * - dispatches `fetchFailure` with the error object on failure.
 */
export const savePermissionTooltip = (data, id) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.post(`${config.API_BASE_URL}/api/v1/permissions/save-permission-details?id=${id}`, data, {
        headers: {
            Authorization: config.getToken(),
            task: admin.permissions.update.task,
            type: admin.permissions.update.type,
        },
    })
        .then((res) => dispatch(updateTooltip(data, res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Fetches notifications from the server based on the provided page number and notification listing page.
 * The function dispatches corresponding actions to manage the state of the notifications list.
 *
 * @param {number} pageNumber - The current page number used to calculate the pagination offset.
 * @param {number} notificationListingPage - Identifier for the specific notification listing page.
 * @returns {Function} A thunk function that dispatches actions to handle the notification fetch process.
 */
export const fetchNotification = (pageNumber, notificationListingPage) => async (dispatch) => {
    dispatch(fetchRequest);
    const limit = 10;
    const skip = limit * pageNumber;
    await axios(`${config.API_BASE_URL}/api/v1/users/notification-listing?skip=${skip}&limit=${limit}&notificationListingPage=${notificationListingPage}`,
        {
            headers: {
                Authorization: config.getToken(),
                task: null,
                type: null,
            },
        })
        .then((res) => dispatch(fetchNotifyList(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Asynchronous action creator to fetch a list of companies.
 *
 * This function dispatches an initial request action, then performs an
 * asynchronous HTTP GET request to retrieve the list of companies from
 * the server. On successful retrieval of the data, it dispatches a
 * success action with the response data.
 *
 * The API endpoint for fetching the company list is retrieved from
 * a configuration object. Authorization headers and optional task and
 * type headers are included in the request.
 *
 * @function fetchCompanies
 * @param {string} [sortBy=''] - The property to sort companies by.
 * @param {string} [order=''] - The sort order ('asc' or 'desc'), defaults to 'desc'.
 * @param {string} [search=''] - Search term to filter companies by name or identifier.
 * @param {boolean|null} [active=null] - Filter companies by active status (true, false, or null for all).
 * @param {number} [skip=-1] - Number of records to skip for pagination.
 * @param {number} [limit=-1] - Maximum number of records to retrieve.
 * @returns {Function} A function that accepts `dispatch` as an argument
 * and performs the asynchronous operation.
 */
export const fetchCompanies = (sortBy = '', order = '', search = '', active = null, skip = -1, limit = -1) => async (dispatch) => {
    dispatch(fetchRequest);
    let params = {
            order: order ?? 'desc'
        },
        paramStr = [];

    if (sortBy) {
        params['sortBy'] = sortBy;
    }
    if (search) {
        params['textSearch'] = search;
    }
    if (active !== null) {
        params['active'] = active;
    }
    if (skip !== -1) {
        params['skip'] = skip;
    }
    if (limit !== -1) {
        params['limit'] = limit;
    }

    for (const [key, value] of Object.entries(params)) {
        paramStr.push(`${key}=${value}`);
    }
    await axios(`${config.API_BASE_URL}/api/v1/company/company-list?${paramStr.join('&')}`,
        {
            headers: {
                Authorization: config.getToken(),
                task: null,
                type: null,
            },
        })
        .then((res) => dispatch(fetchCompaniesSuccess(res)))
};
